import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Send, Plus, Trash2, Loader2, User, Bot } from 'lucide-react';
import api from '../../lib/api';
import { API } from '../../lib/api';

export const AiAssistant = () => {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [meta, setMeta] = useState({ suggestions: [], model: '' });
  const endRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try { const { data } = await api.get('/ai/reports/sessions'); setSessions(data); } catch (e) {}
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/ai/reports/suggestions'); setMeta(data); } catch (e) {}
      loadSessions();
    })();
  }, [loadSessions]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, streaming]);

  const openSession = async (id) => {
    setSid(id);
    try { const { data } = await api.get(`/ai/reports/sessions/${id}`); setMsgs(data.messages || []); } catch (e) { setMsgs([]); }
  };

  const removeSession = async (id) => {
    try { await api.delete(`/ai/reports/sessions/${id}`); } catch (e) {}
    if (id === sid) { setSid(''); setMsgs([]); }
    loadSessions();
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/reports/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, session_id: sid || undefined }),
      });
      if (!res.ok || !res.body) throw new Error('falha');
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let newSid = sid;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const p of parts) {
          const line = p.replace(/^data: /, '').trim();
          if (!line || line === '[DONE]') continue;
          let j;
          try { j = JSON.parse(line); } catch (e) { continue; }
          if (j.session_id && !newSid) { newSid = j.session_id; setSid(j.session_id); }
          if (j.delta) setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: x.content + j.delta } : x)));
          if (j.error) setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: j.error } : x)));
        }
      }
      loadSessions();
    } catch (e) {
      setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: 'Não consegui falar com a IA agora. Tente de novo em instantes.' } : x)));
    } finally { setStreaming(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-4" data-testid="ai-assistant">
      <div className="ent-panel xl:col-span-3 flex flex-col" style={{ minHeight: 560 }}>
        <div className="ent-panel-head">
          <span className="ent-label flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-primary" /> Assistente de dados · {meta.model || 'GPT'}</span>
          <button data-testid="ai-new-chat" onClick={() => { setSid(''); setMsgs([]); }} className="ent-btn ent-btn-sm"><Plus className="h-3 w-3" /> Nova conversa</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages" style={{ maxHeight: 480 }}>
          {!msgs.length && (
            <div className="py-6 text-center">
              <Sparkles className="mx-auto h-7 w-7 text-primary/60" />
              <p className="mt-3 text-sm text-muted-foreground">Pergunte qualquer coisa sobre vendas, leads e estoque — a IA lê os dados reais da loja.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {(meta.suggestions || []).map((s, i) => (
                  <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)} className="ent-pill px-3 py-1.5 text-left hover:border-primary">{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : ''}`} data-testid={`ai-msg-${m.role}`}>
              {m.role === 'assistant' && <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-primary/50 bg-primary/10" style={{ borderRadius: 2 }}><Bot className="h-3.5 w-3.5 text-primary" /></div>}
              <div className={`max-w-[80%] border px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'border-primary/40 bg-primary/[0.07]' : 'border-border bg-muted/20'}`} style={{ borderRadius: 3 }}>
                {m.content || (streaming && i === msgs.length - 1 ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : '')}
              </div>
              {m.role === 'user' && <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-border bg-muted/30" style={{ borderRadius: 2 }}><User className="h-3.5 w-3.5 text-muted-foreground" /></div>}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border p-3 flex gap-2">
          <input data-testid="ai-input" value={input} disabled={streaming} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ex: quanto vendemos em setembro e quem vendeu mais?"
            className="ent-input flex-1" />
          <button data-testid="ai-send" onClick={() => send()} disabled={streaming || !input.trim()} className="ent-btn ent-btn-primary h-8 px-4">
            {streaming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Enviar
          </button>
        </div>
      </div>

      <div className="ent-panel" data-testid="ai-sessions">
        <div className="ent-panel-head"><span className="ent-label">Conversas</span><span className="ent-pill">{sessions.length}</span></div>
        {!sessions.length ? <p className="ent-empty">Nenhuma conversa ainda</p> : (
          <div className="divide-y divide-border max-h-[520px] overflow-y-auto">
            {sessions.map((s) => (
              <div key={s.id} className={`flex items-start gap-2 px-3 py-2.5 ${s.id === sid ? 'bg-primary/[0.06]' : ''}`}>
                <button data-testid={`ai-session-${s.id}`} onClick={() => openSession(s.id)} className="min-w-0 flex-1 text-left">
                  <p className="truncate text-xs font-semibold">{s.title || 'Conversa'}</p>
                  <p className="text-[10px] text-muted-foreground/70">{(s.updated_at || '').slice(0, 16).replace('T', ' ')}</p>
                </button>
                <button data-testid={`ai-del-${s.id}`} onClick={() => removeSession(s.id)} className="ent-btn ent-btn-icon"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
